/**
 * SIL-1429 (slice 4 child 6 of SIL-1319) - the seller-side "Expressed
 * interest" list on the listing detail. Spec section 5: "minimal for this
 * slice" — read-only, owner org only, no reply control (there is no reply
 * path). Slice 5 (SIL-1320) moves this into My Listings.
 *
 * Sourced from `GET /api/client/listings/:id/interests`, which already reads
 * through the anonymized `v_listing_interest_for_seller` view (migration
 * 973) — this component never receives `customerOrgId`/`clientUserId` to
 * begin with, so there is nothing here to accidentally render.
 */
import { useQuery } from '@tanstack/react-query';
import type { ListingInterestForSellerDTO } from '@siliconx/shared/dto/listing-interest';
import { Card } from '@siliconx/shared/ui/Card';
import { PanelTitle } from '@siliconx/shared/ui/PanelTitle';
import { api, throwIfNotOk } from '../../lib/api';
import { qk } from '../../lib/queryKeys';
import { formatDate } from '../../lib/format';
import {
  EXPRESS_INTEREST_TIMELINE_LABELS,
  SELLER_INTEREST_LIST_EMPTY,
  SELLER_INTEREST_LIST_TITLE,
} from '@siliconx/shared/ui/buyer-listing/copy/listingDetail';
import { BUYER_VERIFICATION_ENTITY_TYPE_LABELS } from '@siliconx/shared/ui/buyer-listing/copy/buyerVerification';

async function fetchSellerInterests(listingId: string): Promise<ListingInterestForSellerDTO[]> {
  const res = await api.api.client.listings[':id'].interests.$get({ param: { id: listingId } });
  await throwIfNotOk(res as unknown as Response);
  const body = (await res.json()) as unknown as { data: ListingInterestForSellerDTO[] };
  return body.data;
}

async function fetchRegions(): Promise<{ id: string; name: string }[]> {
  const res = await api.api.client.reference.$get();
  await throwIfNotOk(res as unknown as Response);
  const body = (await res.json()) as unknown as { regions: { id: string; name: string }[] };
  return body.regions;
}

export function ExpressedInterestsPanel({ listingId }: { listingId: string }) {
  const { data, isLoading } = useQuery({
    queryKey: qk.listings.interests(listingId),
    queryFn: () => fetchSellerInterests(listingId),
  });
  // Region NAMES for display — the seller row only ever carries the buyer's
  // stated region.id (never a join back to buyer_org; see migration 973's
  // header), so this app resolves the label itself off the same public
  // catalog `GET /api/client/reference` already uses for the org profile.
  const regionsQ = useQuery({ queryKey: qk.reference(), queryFn: fetchRegions });
  const regionName = (id: string) => regionsQ.data?.find((r) => r.id === id)?.name ?? id;

  const rows = data ?? [];

  return (
    <section>
      <PanelTitle title={SELLER_INTEREST_LIST_TITLE} className="mb-3" />
      {isLoading ? (
        <Card className="text-center text-sm text-muted-foreground">Loading…</Card>
      ) : rows.length === 0 ? (
        <Card className="text-center text-sm text-muted-foreground">{SELLER_INTEREST_LIST_EMPTY}</Card>
      ) : (
        <ul className="divide-y divide-border overflow-hidden rounded-lg border border-border bg-card">
          {rows.map((row) => (
            <li key={row.id} className="px-4 py-3">
              <div className="flex items-center justify-between gap-3">
                <span className="rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-foreground">
                  {row.buyerLabel}
                </span>
                <span className="text-xs text-muted-foreground">{formatDate(row.createdAt)}</span>
              </div>
              <dl className="mt-2 grid grid-cols-1 gap-1 sm:grid-cols-3">
                {row.companyCategory && (
                  <div className="text-xs text-muted-foreground">
                    {BUYER_VERIFICATION_ENTITY_TYPE_LABELS[row.companyCategory] ?? row.companyCategory}
                  </div>
                )}
                {row.timeline && (
                  <div className="text-xs text-muted-foreground">
                    {EXPRESS_INTEREST_TIMELINE_LABELS[row.timeline] ?? row.timeline}
                  </div>
                )}
                {row.buyerRegion && (
                  <div className="text-xs text-muted-foreground">{regionName(row.buyerRegion)}</div>
                )}
              </dl>
              {row.message && <p className="mt-2 text-sm text-foreground">{row.message}</p>}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
